import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, TextField, Button, Collapse, Typography, Link} from 'views/components';
import {HelpOutline} from 'views/icons';
import {translate, Translate} from 'views/module';
import {resetForgotPassword, sendOTPPasswordForgot} from 'utils/auth/forgot_password';
import EditInputAdornment from './components/EditInputAdornment';

const stage1 = 'FirstStage';
const stage2 = 'SecondStage';
const stage3 = 'ThirdStage';

const ForgotPasswordBox = (props) => {
  const {
    setChallengeName,
    email: emailProp = '',

    statusProgressWrap: progressWrap,
  } = props;

  const [email, setEmail] = React.useState(emailProp);
  const [isEmailValid, setIsEmailValid] = React.useState(false);

  // Sync email state with prop changes
  React.useEffect(() => {
    setEmail(emailProp);
    setIsEmailValid(/^\S+@\S+\.\S+$/.test(emailProp));
  }, [emailProp]);

  const [otp, setOtp] = React.useState('');
  const [isOtpValid, setIsOtpValid] = React.useState(false);

  const [password, setPassword] = React.useState('');
  const [isPasswordValid, setIsPasswordValid] = React.useState(false);
  const [isPasswordTouched, setIsPasswordTouched] = React.useState(false);

  const [rePassword, setRePassword] = React.useState('');
  const [isRePasswordValid, setIsRePasswordValid] = React.useState(false);
  const [isRePasswordTouched, setIsRePasswordTouched] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [curStage, setCurStage] = React.useState(stage1);


  const arePasswordsValid = isPasswordTouched && isPasswordValid && isRePasswordTouched && isRePasswordValid && password === rePassword;

  const isButtonEnabled =
    (curStage === stage1 && isEmailValid) ||
    (curStage === stage2 && isOtpValid) ||
    (curStage === stage3 && arePasswordsValid);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (curStage === stage1) {
      if (!isEmailValid) {
        return;
      }
      progressWrap(async () => {
        setLoading(true);
        try {
          const result = await sendOTPPasswordForgot(email);
          if (result.status === 'SUCCESS') {
            setCurStage(stage2);
          } else {
            throw Error;
          }
        } finally {
          setLoading(false);
        }
      }, {
        onErrorMessage: () => {
          return translate('auth.Signup.ForgotPassword.FirstStage.Fail');
        },
      });
    } else if (curStage === stage2) {
      if (!isEmailValid || !isOtpValid) {
        return;
      }
      setCurStage(stage3);
    } else if (curStage === stage3) {
      if (!isEmailValid || !isOtpValid || !arePasswordsValid) {
        return;
      }
      progressWrap(async () => {
        setLoading(true);
        try {
          const result = await resetForgotPassword(email, otp, password);
          if (result.status === 'SUCCESS') {
            setChallengeName('START');
          } else {
            throw Error;
          }
        } finally {
          setLoading(false);
        }
      }, {
        onErrorMessage: () => {
          return translate('auth.Signup.ForgotPassword.FirstStage.Fail');
        },
      });
    }
  };

  const resetToStage = (stage) => {
    if (stage === stage1 || stage=== stage2) {
      setPassword('');
      setRePassword('');
    }
    if (stage === stage1) {
      setOtp('');
    }
    setCurStage(stage);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete='on'
      noValidate
    >
      <Stack
        alignItems="center"
        spacing={{xs: 2, sm: 4}}
      >
        <HelpOutline
          sx={{
            height: {
              xs: 36, sm: 52,
            },
            width: {
              xs: 36, sm: 52,
            },
            color: 'text.contrast',
          }}
        />
        <Stack spacing={1} alignItems="center">
          <Typography variant="h4" fontWeight="normal" align="center" color="text.primary">
            <Translate expression={`auth.Signup.ForgotPassword.${curStage}.Title`} />
          </Typography>
          <Typography variant="body1" color="text.primary" align="center">
            <Translate expression={`auth.Signup.ForgotPassword.${curStage}.Body`} />
          </Typography>
        </Stack>


        <TextField
          label={translate('auth.Signup.ForgotPassword.FirstStage.Email')}
          type='email'
          name='email'
          autoComplete='email'
          fullWidth
          value={email}
          onChange={setEmail}
          InputLabelProps={{shrink: true}}
          disabled={loading || curStage !== stage1}
          validation={{
            conditions: [
              [
                (val) => {
                  return /^\S+@\S+\.\S+$/.test(val);
                },
                translate('auth.Signup.ForgotPassword.Errors.Email'),
              ],
            ],
            update: setIsEmailValid,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
          size='medium'
          slotProps={{
            input: {
              endAdornment: <EditInputAdornment
                in={curStage !== stage1}
                onClick={() => resetToStage(stage1)}
              />,
            },
          }}
        />
        <Collapse
          in={curStage === stage2 || curStage === stage3}
          unmountOnExit
          sx={{
            width: '100%',
          }}
        >
          <TextField
            label={translate('auth.Signup.ForgotPassword.SecondStage.OTP')}
            name='otp'
            fullWidth
            value={otp}
            onChange={setOtp}
            InputLabelProps={{shrink: true}}
            disabled={loading || curStage !== stage2}
            validation={{
              conditions: [
                [
                  (val) => {
                    return /^\d{6}$/.test(val);
                  },
                  translate('auth.Signup.ForgotPassword.Errors.OTP'),
                ],
              ],
              update: setIsOtpValid,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            size='medium'
            slotProps={{
              input: {
                endAdornment: <EditInputAdornment
                  in={curStage === stage3}
                  onClick={() => resetToStage(stage2)}
                />,
              },
            }}
          />
        </Collapse>
        <Collapse
          in={curStage === stage3}
          unmountOnExit
          sx={{
            width: '100%',
          }}
        >
          <TextField
            label={translate('auth.Signup.ForgotPassword.ThirdStage.Password')}
            type='password'
            name='password'
            autoComplete='new-password'
            fullWidth
            value={password}
            onChange={setPassword}
            InputLabelProps={{shrink: true}}
            disabled={loading}
            validation={{
              conditions: [[((val) => val.length >= 6), translate('auth.Signup.ForgotPassword.Errors.Password.Length')]],
              update: setIsPasswordValid,
              touch: () => setIsPasswordTouched(true),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            size='medium'
          />
        </Collapse>
        <Collapse
          in={curStage === stage3}
          unmountOnExit
          sx={{
            width: '100%',
          }}
        >
          <TextField
            label={translate('auth.Signup.ForgotPassword.ThirdStage.RePassword')}
            type='password'
            name='rePassword'
            autoComplete='new-password'
            fullWidth
            value={rePassword}
            onChange={setRePassword}
            InputLabelProps={{shrink: true}}
            disabled={loading}
            validation={{
              conditions: [
                [((val) => val.length >= 6), translate('auth.Signup.UserAuth.ResetPassword.Errors.Length')],
                [((val) => val === password), translate('auth.Signup.UserAuth.ResetPassword.Errors.Match')],
              ],
              update: setIsRePasswordValid,
              touch: () => setIsRePasswordTouched(true),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            size='medium'
          />
        </Collapse>
        <Button
          type='submit'
          variant={!isButtonEnabled ? 'outlined' : 'contained'}
          fullWidth
          disabled={!isButtonEnabled}
          loading={loading}
          sx={{
            'py': 2,
            'borderRadius': 2,
            'textTransform': 'none',
            'backgroundColor': !isButtonEnabled ? 'color.white' : '#DA7955',
            'color': !isButtonEnabled ? '#2D2925B8' : 'color.white',
          }}
        >
          <Translate expression={`auth.Signup.ForgotPassword.${curStage}.Action`} />
        </Button>
        <Link
          variant="body1"
          onClick={() => setChallengeName('START')}
        >
          <Translate expression='auth.Signup.ForgotPassword.GoBack.GoBack' />
        </Link>
      </Stack>
    </Box>
  );
};

ForgotPasswordBox.propTypes = {
  setChallengeName: PropTypes.func.isRequired,
  email: PropTypes.string,

  statusProgressWrap: PropTypes.func.isRequired,
  statusClear: PropTypes.func.isRequired,
  userReload: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, dispatch)(React.memo(ForgotPasswordBox));
